import {
  useState,
  ChangeEventHandler,
  useCallback,
  KeyboardEventHandler,
  FC,
  ReactNode
} from 'react';

import classNames from 'classnames';

import { FilterChip } from 'components';
import { FormError } from 'components/FormError/FormError';
import { If } from 'components/Generics';
import { EMAIL_REGEX, KEYBOARD_KEYS } from 'constants/common';

export type EmailSelectorProps = {
  emailLimit?: number;
  defaultMailId?: string;
  label?: string;
  endAdornment?: ReactNode;
  emailList?: string[];
  setEmails?: (emailList: string[]) => void;
  name?: string;
  placeholder?: string;
  autoFocus?: boolean;
};

export const EmailSelectorInput: FC<EmailSelectorProps> = ({
  emailLimit = 0,
  defaultMailId = '',
  label = 'Email',
  endAdornment = null,
  emailList = [],
  setEmails,
  name = '',
  placeholder = '',
  autoFocus
}) => {
  const [text, setText] = useState('');

  const onChangeText: ChangeEventHandler<HTMLInputElement> = useCallback(
    (event) => setText(event.target.value),
    []
  );

  const addTextToEmailList = useCallback(() => {
    const newEmailList = [...emailList, text];
    setEmails?.(newEmailList);
    setText('');
  }, [emailList, setEmails, text]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (event) => {
      if (
        text &&
        event.key === KEYBOARD_KEYS.ENTER &&
        !emailList?.includes(text)
      ) {
        addTextToEmailList();
      }
      if (!text && event.key === KEYBOARD_KEYS.BACKSPACE) {
        const newEmailList = [...emailList];
        newEmailList?.pop();
        setEmails?.(newEmailList);
      }
    },
    [addTextToEmailList, emailList, setEmails, text]
  );

  const handleClose: (emailId: string) => void = useCallback(
    (deletedEmailId) => {
      const newEmailList = emailList?.filter(
        (email) => email !== deletedEmailId
      );
      setEmails?.(newEmailList);
    },
    [emailList, setEmails]
  );

  let isEmailListInvalid = false;

  const handleBlur = () => {
    if (text) addTextToEmailList();
  };

  return (
    <div className='text-sm w-full'>
      <label htmlFor={name}>
        <span className='mb-2 text-xs md:text-sm'>{label}</span>
        <div className='w-full cursor-text'>
          <div className='flex justify-between items-center gap-2 px-4 py-3 border-1 border-CORE_GRAY rounded-8px  text-DEFAULT_TEXT w-full'>
            <div className='flex gap-2 flex-wrap w-11/12 min-h-6'>
              <If condition={!!defaultMailId}>
                <FilterChip
                  handleClose={() => null}
                  label={defaultMailId}
                  disabled
                  className='rounded-full px-3 py-0'
                />
              </If>
              {emailList?.map((email) => {
                const isEmailValid = EMAIL_REGEX?.test(email);
                if (!isEmailValid) isEmailListInvalid = true;
                return (
                  <FilterChip
                    key={email}
                    handleClose={() => handleClose(email)}
                    label={email}
                    className='rounded-full px-3 py-0 bg-transparent border-1 border-DEFAULT_TEXT'
                    error={!isEmailValid}
                  />
                );
              })}
              <If condition={!emailLimit || emailList?.length < emailLimit}>
                <input
                  id={name}
                  value={text}
                  onChange={onChangeText}
                  onKeyDown={handleKeyDown}
                  className={classNames(
                    'max-w-full w-auto flex-1 placeholder-COOL_GREY',
                    { 'md:text-17px': !emailList?.length && !text }
                  )}
                  placeholder={
                    !emailList?.length && !defaultMailId ? placeholder : ''
                  }
                  onBlur={handleBlur}
                  // eslint-disable-next-line jsx-a11y/no-autofocus
                  autoFocus={autoFocus}
                />
              </If>
            </div>
            <div className='flex'>{endAdornment}</div>
          </div>
        </div>
      </label>
      <If condition={isEmailListInvalid}>
        <FormError errorMessage='consent_form.invalid_email_address_added' />
      </If>
    </div>
  );
};
